import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import { generateClassName } from '@klv/shared/utils/functions';
import { SelectFieldGroup } from '@klv/molecules/form-field/select';
import { Select } from '@klv/molecules/form-field';
import Icon from '@klv/atoms/icon';

import { 
  getAllCountries, 
  calculateEffectiveCredits, 
  calculatePricingInfo, 
  getSelectedFee, 
  isSmsAvailable,
  isMmsAvailable, 
  getOptionRatio, 
  hasCarrierFees,
  isCreditTypeAvailable,
  type TCreditType
} from './utils';

import * as styles from './styles/pricing-calulator.module.css';
import type { IPricingCalc, TValidLocales } from './types';

const CREDIT_TYPES = ['sms', 'mms', 'whatsapp-marketing', 'whatsapp-transactional'] as const;


const PricingCalculator = ({
  messages,
  backgroundColor,
  className,
  selectedLocale: externalSelectedLocale,
  onToggleChange,
  onLocaleChange,
  ...props
}: IPricingCalc) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  
  const pathPrefix = t('langCodes_pathPrefix');
  const siteLocale = pathPrefix === "" ? "US" : pathPrefix.toUpperCase();

  const [creditType, setCreditType] = useState<TCreditType>('sms');
  const [selectedLocale, setSelectedLocale] = useState(externalSelectedLocale || siteLocale);
  const [isOpen, setIsOpen] = useState(false);

  // Sync internal locale state with parent prop changes
  useEffect(() => {
    if (externalSelectedLocale && externalSelectedLocale !== selectedLocale) {
      setSelectedLocale(externalSelectedLocale);
    }
  }, [externalSelectedLocale, selectedLocale]);

  const countries = getAllCountries();

  // Calculate the required credit tier based on messages
  const effectiveCredits = calculateEffectiveCredits(messages || 0, selectedLocale, creditType);
  
  const effectiveCreditsString = new Intl.NumberFormat(locale).format(effectiveCredits);
  
  const { pricePerSend } = calculatePricingInfo(effectiveCredits, selectedLocale, creditType);

  const selectedFee: number = getSelectedFee(creditType, selectedLocale);

  const creditString = creditType === 'mms'
    ? t('organisms_pricing_calculator_mms')
    : creditType === 'whatsapp-marketing'
    ? t('organisms_pricing_calculator_whatsapp_marketing')
    : creditType === 'whatsapp-transactional'
    ? t('organisms_pricing_calculator_whatsapp_transactional')
    : t('organisms_pricing_calculator_sms');


  // Format price per send for display
  const pricePerSendFormatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(pricePerSend);

  const carrierFee = selectedFee ? new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(selectedFee) : '';

  const toggleCollapse = () => setIsOpen(!isOpen);

  const handleToggle = useCallback((newCreditType: TCreditType) => {
    setCreditType(newCreditType);
    onToggleChange(newCreditType);
  }, [onToggleChange]);

  const renderEffectiveCredits = () => {
    if (creditType === 'mms' && !isMmsAvailable(selectedLocale)) {
      return null;
    }

    // Hide credits for WhatsApp Marketing and Transactional in US
    if ((creditType === 'whatsapp-marketing' || creditType === 'whatsapp-transactional') && selectedLocale === 'US') {
      return null;
    }

    return (
      <>
        <div className={styles.maxSends}>
          {effectiveCreditsString}
          {' '}
          {t('organisms_pricing_calculator_credits')}
        </div>
        <div className={styles.pricePerSend}>
          ({t('organisms_pricing_calculator_pricePerSend')}: {pricePerSendFormatted})
        </div>
      </>
    );
  };

  const renderCarrierFeeDisclaimer = () => {
    if (creditType === 'mms' && !isMmsAvailable(selectedLocale)) {
      return null;
    }

    if (!hasCarrierFees(creditType, selectedLocale)) {
      return null;
    }

    return (
      <div className={styles.disclaimerContainer}>
        <div className={styles.disclaimer}>
          <div className={styles.asterisk}>*</div>
          <div>
            {t('organisms_pricing_calculator_carrierFeeDisclaimer')}{' '}
            {carrierFee}.{' '}
            {t('organisms_pricing_calculator_carrierFeeDisclaimerEnd')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={generateClassName([className, styles.pricingCalc, isOpen ? styles.openAccordion: ''])} {...props}>
      <div 
        className={!isOpen ? styles.closedCollapsibleHeader : styles.openCollapsibleHeader}
        onClick={toggleCollapse}
      >
        <div className={styles.headline}>{t('organisms_pricing_calculator_headline')}</div>
        <Icon
          iconName="ChevronDown"
          className={generateClassName([styles.chevron, isOpen ? styles.open : ''])}
        />
      </div>

      <div className={generateClassName([styles.messagingContainer, isOpen ? styles.messagingContainerOpen : styles.messagingContainerClosed])}>
        <div className={styles.flexContainer}>
          <div className={styles.destination}>
            <div className={generateClassName(['tagline', styles.label])}>
              {t('organisms_pricing_calculator_destination')}
            </div>
            <div className={styles.dropdownContainer}>
              <SelectFieldGroup className={styles.dropdown} backgroundColor="light" pricing={true}>
                <Icon className={styles.icon} iconName="ChevronDown" />
                <Select
                  className={styles.input}
                  $backgroundColor={backgroundColor}
                  aria-labelledby="pricing--sms-country"
                  hasError={false}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const newLocale = event.target.value as TValidLocales;
                    if (onLocaleChange) {
                      onLocaleChange(newLocale);
                    } else {
                      setSelectedLocale(newLocale);
                    }
                  }}
                  value={selectedLocale as TValidLocales}
                >
                  {countries
                    .sort((a, b) => {
                      const nameA = t(`organisms_pricing_calculator_locales_${a}`);
                      const nameB = t(`organisms_pricing_calculator_locales_${b}`);
                      return nameA.localeCompare(nameB);
                    })
                    .map((code) => {
                      const optionRatio = getOptionRatio(creditType, code);
                      const isCodeAvailable = isCreditTypeAvailable(creditType, code);
                      return (
                        <option
                          className={styles.selectedLocale}
                          key={code}
                          value={code}
                          disabled={!isCodeAvailable}
                        >
                          {t(`organisms_pricing_calculator_locales_${code}`)} (
                          {!isCodeAvailable
                            ? t('organisms_pricing_calculator_not_available')
                            : `${optionRatio} ${
                              optionRatio === 1
                                ? t('organisms_pricing_calculator_credit')
                                : t('organisms_pricing_calculator_credits')
                            }/${creditString}`}
                          )
                        </option>
                      );
                    })}
                </Select>
              </SelectFieldGroup>
            </div>
          </div>

          <div className={styles.toggleContainer}>
            <div className={generateClassName(['tagline', styles.desktopTypeLabel])}>
              {t('organisms_pricing_calculator_service_type')}
            </div>
            <div className={styles.segmentedControl}>
              <button
                type="button"
                className={generateClassName([styles.segment, creditType === 'sms' ? styles.active : ''])}
                onClick={() => handleToggle('sms')}
              >
                {t('organisms_pricing_calculator_sms')}
              </button>
              <button
                type="button"
                className={generateClassName([styles.segment, creditType === 'mms' ? styles.active : ''])}
                onClick={() => handleToggle('mms')}
              >
                {t('organisms_pricing_calculator_mms')}
              </button>
              <button
                type="button"
                className={generateClassName([styles.segment, creditType === 'whatsapp-marketing' ? styles.active : ''])}
                onClick={() => handleToggle('whatsapp-marketing')}
              >
                {t('organisms_pricing_calculator_whatsapp_marketing')}
              </button>
              <button
                type="button"
                className={generateClassName([styles.segment, creditType === 'whatsapp-transactional' ? styles.active : ''])}
                onClick={() => handleToggle('whatsapp-transactional')}
              >
                {t('organisms_pricing_calculator_whatsapp_transactional')}
              </button>
            </div>
          </div>
        </div>
        <div className={styles.desktop}>
          <div className={styles.creditInfoContainer}>
            <div className={styles.leftSection}>
                <div className={styles.creditType}>{creditString}</div>
              {['sms', 'mms', 'whatsapp-marketing', 'whatsapp-transactional'].includes(creditType) && (
                <>
                  {(creditType === 'sms' && !isSmsAvailable(selectedLocale)) || 
                   (creditType === 'mms' && !isMmsAvailable(selectedLocale)) ? (
                    <div className={styles.notAvailable}>
                      {creditType === 'sms' && t('organisms_pricing_calculator_sms_not_available')}
                      {creditType === 'mms' && t('organisms_pricing_calculator_mms_not_available')}
                    </div>
                  ) : (
                    <div className={styles.characterLimit}>
                      {creditType === 'sms' && t('organisms_pricing_calculator_sms_character_limit')}
                      {creditType === 'mms' && t('organisms_pricing_calculator_mms_character_limit')}
                      {creditType === 'whatsapp-marketing' && (
                        selectedLocale === 'US' 
                          ? t('organisms_pricing_calculator_whatsapp_marketing_messages')
                          : t('organisms_pricing_calculator_whatsapp_marketing_messages_general')
                      )}
                      {creditType === 'whatsapp-transactional' && t('organisms_pricing_calculator_whatsapp_transactional_messages')}
                    </div>
                  )}
                </>
              )}
              {creditType === "mms" || creditType === "sms" ? renderCarrierFeeDisclaimer() : null}
            </div>
            <div className={styles.rightSection}>
              {renderEffectiveCredits()}
            </div>
          </div>
        </div>

        <div className={styles.mobile}>
          <div className={styles.mobileContainer}>
            <div className={styles.mobileInputs}>

              <div className={styles.type}>
                <div className={generateClassName(['tagline', styles.mobileLabel])}>
                  {t('organisms_pricing_calculator_service_type')}
                </div>
                <div className={styles.dropdownContainer}>
                  <SelectFieldGroup className={styles.dropdown} backgroundColor="light">
                    <Icon className={styles.icon} iconName="ChevronDown" />
                    <Select
                      className={styles.input}
                      $backgroundColor={backgroundColor}
                      aria-labelledby="pricing--credit-type-mobile"
                      hasError={false}
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                        handleToggle(event.target.value as TCreditType)
                      }
                      value={creditType}
                    >
                      <option value="sms">{t('organisms_pricing_calculator_sms')}</option>
                      <option value="mms">{t('organisms_pricing_calculator_mms')}</option>
                      <option value="whatsapp-marketing">{t('organisms_pricing_calculator_whatsapp_marketing')}</option>
                      <option value="whatsapp-transactional">{t('organisms_pricing_calculator_whatsapp_transactional')}</option>
                    </Select>
                  </SelectFieldGroup>
                </div>
              </div>
            </div>

            <div className={styles.mobileContent}>
              {(creditType === 'sms' && !isSmsAvailable(selectedLocale)) || 
                (creditType === 'mms' && !isMmsAvailable(selectedLocale)) ? (
                <div className={styles.notAvailable}>
                  {creditType === 'sms' && t('organisms_pricing_calculator_sms_not_available')}
                  {creditType === 'mms' && t('organisms_pricing_calculator_mms_not_available')}
                </div>
              ) : (
                <>
                  {/* Hide credits for WhatsApp Marketing and Transactional in US */}
                  {!((creditType === 'whatsapp-marketing' || creditType === 'whatsapp-transactional') && selectedLocale === 'US') && (
                    <>
                      <div className={styles.mobileCredits}>
                        {effectiveCreditsString}
                        {' '}
                        {t('organisms_pricing_calculator_credits')}
                      </div>
                      <div className={styles.mobilePricePerSend}>
                        ({t('organisms_pricing_calculator_pricePerSend')}: {pricePerSendFormatted})
                      </div>
                    </>
                  )}
                  {CREDIT_TYPES.includes(creditType) && (
                    <div className={styles.characterLimit}>
                      {creditType === 'sms' && t('organisms_pricing_calculator_sms_character_limit')}
                      {creditType === 'mms' && t('organisms_pricing_calculator_mms_character_limit')}
                      {creditType === 'whatsapp-marketing' && (
                        selectedLocale === 'US' 
                          ? t('organisms_pricing_calculator_whatsapp_marketing_messages')
                          : t('organisms_pricing_calculator_whatsapp_marketing_messages_general')
                      )}
                      {creditType === 'whatsapp-transactional' && t('organisms_pricing_calculator_whatsapp_transactional_messages')}
                    </div>
                  )}
                  {renderCarrierFeeDisclaimer()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;

